import React from "react";
import { Box, Container, Typography, Grid, Paper, Avatar } from "@mui/material";
import PhoneIcon from "@mui/icons-material/Phone";
import EmailIcon from "@mui/icons-material/Email";
import VerifiedIcon from "@mui/icons-material/Verified";
import SecurityIcon from "@mui/icons-material/Security";
import AttachMoneyIcon from "@mui/icons-material/AttachMoney";
import GavelIcon from "@mui/icons-material/Gavel";
import LightbulbIcon from "@mui/icons-material/Lightbulb";
import WorkspacePremiumIcon from "@mui/icons-material/WorkspacePremium";
import MyLocationIcon from "@mui/icons-material/MyLocation";
import { useTheme } from "../../../context/ThemeContext";

const AboutUsSection = () => {
  const { theme } = useTheme();

  const coreValues = [
    { icon: <VerifiedIcon sx={{ fontSize: 24, color: "#fff" }} />, title: "Transparency", description: "Clear records, no hidden terms", color: "#2196f3" },
    { icon: <SecurityIcon sx={{ fontSize: 24, color: "#fff" }} />, title: "Trust", description: "Reliable relationships", color: "#4caf50" },
    { icon: <AttachMoneyIcon sx={{ fontSize: 24, color: "#fff" }} />, title: "Affordability", description: "Quality housing for everyone", color: "#ff9800" },
    { icon: <GavelIcon sx={{ fontSize: 24, color: "#fff" }} />, title: "Accountability", description: "Every payment documented", color: "#9c27b0" },
    { icon: <LightbulbIcon sx={{ fontSize: 24, color: "#fff" }} />, title: "Innovation", description: "Technology-driven solutions", color: "#00bcd4" },
    { icon: <WorkspacePremiumIcon sx={{ fontSize: 24, color: "#fff" }} />, title: "Integrity", description: "Honest dealings always", color: "#f44336" },
  ];

  const contactItems = [
    { icon: <PhoneIcon sx={{ fontSize: 28 }} />, label: "Phone", value: "+92-349-2565577", color: "#4caf50" },
    { icon: <EmailIcon sx={{ fontSize: 28 }} />, label: "Email", value: "ownertenantsolution@gmail.com", color: "#2196f3" },
    { icon: <MyLocationIcon sx={{ fontSize: 28 }} />, label: "Address", value: "House# B38, Street# 03, Block# 09, Bhittaiabad, Gulistan-E-Jauhar, Karachi.", color: "#f093fb" },
  ];

  return (
    <Box id="about-us" sx={{ py: { xs: 8, md: 12 }, bgcolor: "#f8f9fa" }}>
      <Container maxWidth="lg">
        {/* Header */}
        <Box sx={{ textAlign: "center", mb: 7 }}>
          <Typography variant="overline" sx={{ color: theme.primary, fontWeight: 700, letterSpacing: 3, fontSize: "0.75rem" }}>
            ABOUT US
          </Typography>
          <Typography variant="h3" fontWeight={800} sx={{ mt: 1, color: "#1a1a2e" }}>
            Who We Are
          </Typography>
          <Box sx={{ width: 60, height: 4, bgcolor: theme.primary, mx: "auto", mt: 2, borderRadius: 99 }} />
        </Box>

        {/* Intro */}
        <Paper elevation={0} sx={{
          p: { xs: 3, md: 5 }, mb: 8, borderRadius: 4,
          background: `linear-gradient(135deg, ${theme.primary}10 0%, ${theme.primary}04 100%)`,
          border: `1px solid ${theme.primary}20`,
          textAlign: "center",
        }}>
          <Typography variant="h6" sx={{ lineHeight: 1.9, color: "#444", fontWeight: 400 }}>
            <Box component="span" sx={{ fontWeight: 700, color: theme.primary }}>Owner Tenant Solution (OTS)</Box> is Pakistan's
            innovative property management platform designed to bridge the gap between property owners and tenants.
            We help find affordable housing, maintain complete financial records of advance payments, monthly rent, and
            all transactions — ensuring transparency and trust for everyone.
          </Typography>
        </Paper>

        {/* Core Values */}
        <Typography variant="h5" fontWeight={700} textAlign="center" sx={{ mb: 4, color: "#1a1a2e" }}>
          Our Core Values
        </Typography>
        <Grid container spacing={2.5} sx={{ mb: 8 }}>
          {coreValues.map((value, index) => (
            <Grid item xs={6} sm={4} md={2} key={index}>
              <Paper elevation={0} sx={{
                p: 2.5, textAlign: "center", borderRadius: 3,
                border: "1px solid #eee",
                transition: "all 0.3s",
                "&:hover": { transform: "translateY(-6px)", boxShadow: "0 12px 30px rgba(0,0,0,0.08)", borderColor: value.color },
              }}>
                <Avatar sx={{ bgcolor: value.color, width: 50, height: 50, mx: "auto", mb: 1.5 }}>
                  {value.icon}
                </Avatar>
                <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 0.3 }}>
                  {value.title}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {value.description}
                </Typography>
              </Paper>
            </Grid>
          ))}
        </Grid>

        {/* Contact Card */}
        <Paper elevation={0} sx={{
          p: { xs: 4, md: 5 }, borderRadius: 4,
          background: "linear-gradient(135deg, #0f0f23 0%, #1a1a3e 100%)",
          color: "white",
          position: "relative",
          overflow: "hidden",
          "&::before": {
            content: '""', position: "absolute", top: -60, right: -60,
            width: 200, height: 200, borderRadius: "50%",
            background: `${theme.primary}15`,
          },
        }}>
          <Typography variant="h5" fontWeight={700} textAlign="center" gutterBottom sx={{ mb: 4 }}>
            Contact Us
          </Typography>
          <Grid container spacing={3} justifyContent="center">
            {contactItems.map((item, i) => (
              <Grid item xs={12} sm={4} key={i}>
                <Box sx={{ display: "flex", alignItems: "flex-start", gap: 2 }}>
                  <Box sx={{
                    color: item.color, bgcolor: `${item.color}15`,
                    p: 1, borderRadius: 2, display: "flex", flexShrink: 0, mt: 0.3,
                  }}>
                    {item.icon}
                  </Box>
                  <Box>
                    <Typography variant="caption" sx={{ opacity: 0.5, display: "block", mb: 0.3 }}>
                      {item.label}
                    </Typography>
                    <Typography variant="body2" fontWeight={600} sx={{ lineHeight: 1.6 }}>
                      {item.value}
                    </Typography>
                  </Box>
                </Box>
              </Grid>
            ))}
          </Grid>
        </Paper>
      </Container>
    </Box>
  );
};

export default AboutUsSection;